import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  IHistorialProceso,
  getHistorialProceso,
} from "../../../../services/historialProcesoService";
import {
  ESTADOS_GESTION_INGRESO,
  resolveEstadoGestionIngreso,
} from "../../../../utils/estadosGestionIngreso";
import "./gestion-ingreso.css";

interface IHistorialProcesoProps {
  busPerId: string;
}

const formatFecha = (fecha: string): string => {
  const [dia, hora] = (fecha ?? "").split("T");
  if (!dia) return "-";
  const [year, month, day] = dia.split("-");
  return `${day}/${month}/${year}${hora ? ` ${hora.slice(0, 5)}` : ""}`;
};

const etiquetaEstado = ({ estado, descripcionEstado }: IHistorialProceso): string =>
  ESTADOS_GESTION_INGRESO.includes(estado)
    ? resolveEstadoGestionIngreso(estado).label
    : descripcionEstado || estado;

/**
 * Bitácora de un registro: cada cambio de estado del flujo, del más reciente
 * al más antiguo. La nota de un retroceso viaja dentro del comentario (después
 * de " | ") y se destaca aparte para que se lea de un vistazo.
 */
const HistorialProceso = ({ busPerId }: IHistorialProcesoProps) => {
  const [historial, setHistorial] = useState<IHistorialProceso[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    let vigente = true;
    setIsLoading(true);
    setIsError(false);
    getHistorialProceso(busPerId)
      .then((data) => vigente && setHistorial(data))
      .catch(() => vigente && setIsError(true))
      .finally(() => vigente && setIsLoading(false));
    return () => {
      vigente = false;
    };
  }, [busPerId]);

  if (isLoading) {
    return (
      <div className="gi-historial-estado">
        <CircularProgress size={22} thickness={4} sx={{ color: "#0065DD" }} />
      </div>
    );
  }

  if (isError) {
    return <div className="gi-historial-estado">No se pudo cargar el historial.</div>;
  }

  if (historial.length === 0) {
    return <div className="gi-historial-estado">Sin movimientos registrados.</div>;
  }

  return (
    <ol className="gi-historial">
      {historial.map((item, index) => {
        const [comentario, ...notas] = (item.comentario ?? "").split(" | ");
        return (
          <li key={`${item.fecha}-${index}`} className="gi-historial-item">
            <span className={`gi-historial-punto${notas.length > 0 ? " nota" : ""}`} />
            <div className="gi-historial-cuerpo">
              <div className="gi-historial-cabecera">
                <span className="gi-historial-titulo">{etiquetaEstado(item)}</span>
                <span className="gi-historial-fecha">{formatFecha(item.fecha)}</span>
              </div>
              <span className="gi-historial-texto">{comentario}</span>
              {notas.map((nota, i) => (
                <span key={i} className="gi-historial-nota">{nota}</span>
              ))}
              {item.usuario && <span className="gi-historial-usuario">{item.usuario}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default HistorialProceso;
